import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const sections = [
  {
    title: 'Acceptance of Terms',
    icon: 'ri-file-check-line',
    content: `By accessing or using SwiftTrack\'s shipment tracking platform and related services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please discontinue use of our Services immediately.

    These Terms apply to all users of our Services, including visitors, customers, and administrators. SwiftTrack reserves the right to modify these Terms at any time. Continued use after modifications constitutes acceptance.`,
  },
  {
    title: 'Description of Services',
    icon: 'ri-truck-line',
    content: `SwiftTrack provides:
    
    • Real-time shipment tracking and status updates
    • Shipment creation and management tools for authorized administrators
    • Interactive route mapping and delivery timeline visualization
    • Automated email notifications for shipment status changes
    • QR code scanning for instant package lookup
    • Admin portal for logistics management
    
    Services are provided "as-is" and may be modified, suspended, or discontinued at any time.`,
  },
  {
    title: 'User Responsibilities',
    icon: 'ri-user-line',
    content: `When using SwiftTrack services, you agree to:
    
    • Provide accurate and truthful information for all shipment records
    • Not use our platform to track shipments fraudulently or without authorization
    • Keep your admin credentials confidential and not share access with unauthorized parties
    • Not attempt to circumvent, reverse-engineer, or exploit vulnerabilities in our system
    • Comply with all applicable local, national, and international laws and regulations
    • Not upload or transmit harmful, illegal, or malicious content through our platform`,
  },
  {
    title: 'Prohibited Uses',
    icon: 'ri-forbid-line',
    content: `The following uses of SwiftTrack are strictly prohibited:
    
    • Tracking individuals without their knowledge or consent
    • Using our platform for illegal shipments including prohibited goods
    • Attempting unauthorized access to admin or database systems
    • Scraping, crawling, or bulk data extraction without written permission
    • Impersonating SwiftTrack staff or creating fake shipment records for fraudulent purposes
    • Overloading our systems through automated requests or denial-of-service attacks
    
    Violations may result in immediate account termination and legal action.`,
  },
  {
    title: 'Intellectual Property',
    icon: 'ri-copyright-line',
    content: `All content, features, and functionality of the SwiftTrack platform including but not limited to:
    
    • Software code, algorithms, and database structures
    • Visual design, logos, and brand elements
    • Text content, documentation, and help materials
    
    Are the exclusive intellectual property of SwiftTrack and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without express written permission.`,
  },
  {
    title: 'Limitation of Liability',
    icon: 'ri-scales-line',
    content: `SwiftTrack shall not be liable for:
    
    • Delays, inaccuracies, or failures in shipment status updates due to carrier or network issues
    • Loss or damage to packages — liability rests with the shipping carrier
    • Indirect, incidental, special, or consequential damages arising from use of our Services
    • Data loss resulting from technical failures, provided reasonable backup measures are in place
    • Actions taken by third-party carriers or logistics partners
    
    Our maximum liability for any claim is limited to the amount paid for the specific service in the prior 3 months.`,
  },
  {
    title: 'Termination',
    icon: 'ri-close-circle-line',
    content: `SwiftTrack may suspend or terminate your access to Services at any time, with or without notice, for:
    
    • Violation of these Terms of Service
    • Fraudulent, abusive, or illegal activity
    • Non-payment of applicable service fees
    • Requests from law enforcement or government agencies
    
    Upon termination, your right to use the Services ceases immediately. Data retention follows our Privacy Policy.`,
  },
  {
    title: 'Governing Law',
    icon: 'ri-government-line',
    content: `These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions. Any disputes arising from these Terms or use of our Services shall be resolved through binding arbitration, except where prohibited by law.

    For business customers in the European Union, GDPR provisions take precedence where applicable.
    
    Contact us at legal@swifttrack.com for any legal inquiries.`,
  },
];

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-28 pb-14" style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, var(--primary) 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <i className="ri-file-text-line text-white text-2xl" />
          </div>
          <h1 className="font-heading font-bold text-4xl text-white mb-4">Terms of Service</h1>
          <p className="text-white/70 text-base max-w-xl mx-auto leading-relaxed">
            Please read these terms carefully before using SwiftTrack&apos;s shipment tracking and logistics services.
          </p>
          <p className="text-white/40 text-sm mt-4">Last updated: March 25, 2026</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-6 pt-8 pb-2">
        <nav className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-600">Terms of Service</span>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 pb-20">
        <div className="bg-teal-50 border border-teal-200/60 rounded-2xl p-5 mb-10 flex items-start gap-4">
          <i className="ri-information-line text-teal-600 text-xl flex-shrink-0 mt-0.5" />
          <p className="text-teal-800 text-sm leading-relaxed">
            These Terms of Service govern your use of the SwiftTrack platform. By creating a shipment, tracking a package, or accessing any part of our system, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </p>
        </div>

        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="border border-gray-100 rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-light)' }}>
                  <i className={`${section.icon} text-base`} style={{ color: 'var(--primary)' }} />
                </div>
                <h2 className="font-heading font-semibold text-lg text-gray-900">{section.title}</h2>
              </div>
              <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line pl-12">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-wrap gap-4 justify-center">
          <Link to="/privacy-policy" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Privacy Policy
          </Link>
          <span className="text-gray-300">·</span>
          <Link to="/cookie-policy" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Cookie Policy
          </Link>
          <span className="text-gray-300">·</span>
          <Link to="/contact" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Contact Us
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
